import { TextFieldProps } from '@mui/material/TextField/TextField';
import { useFormContext } from 'react-hook-form';
import { forwardRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import { InputAdornment, Stack, SxProps, Theme } from '@mui/material';
import { formatAmount } from '../../utils/helpers';
import { secondaryBlue } from '../../utils/color';

type TProps = {
  name: string;
  required?: boolean;
  initialValue: number;
  adornmentText?: string;
  adornmentSx?: SxProps<Theme>;
} & TextFieldProps;

const inputAdornmentStyles = {
  paddingRight: '1px',
  '.MuiTypography-root': {
    fontSize: '14px',
    fontFamily: 'Gellix-SemiBold',
    color: 'sprimaryDark.600',
  },
};

const styles = {
  '.MuiButtonBase-root': {
    display: 'none',
  },
  '.MuiInputBase-formControl': {
    border: `1px solid ${secondaryBlue[100]}`,
    color: 'primaryDark.600',
    backgroundColor: 'transparent',
    paddingRight: 0,
    fontSize: '14px',
    '&:not(.Mui-disabled)': {
      '&:hover': {
        borderColor: 'primaryLight.200',
      },
    },
  },
  input: {
    fontSize: '14px',
    padding: '0.844rem 0',
  },
  '.Mui-focused': {
    borderColor: 'primaryLight.500',
  },
  '.MuiFormHelperText-root': {
    marginLeft: 0,
    boxShadow: 'none !important',
    color: 'secondaryPink.500',
    marginTop: '0 !important',
    lineHeight: 1,
  },
  fieldset: {
    border: '0 !important',
  },
  '.Mui-disabled': {
    backgroundColor: 'primaryDark.200',
    color: 'primaryDark.300',
    border: 'none',
  },
};

const ControlledContractMilestoneNumberInput = forwardRef(
  (
    { name, label, required, initialValue, sx, adornmentText, disabled, adornmentSx }: TProps,
    ref: any,
  ) => {
    const roundedInitialValue =
      Math.round((parseFloat(initialValue.toString()) + Number.EPSILON) * 100) / 100;
    const { setValue } = useFormContext();
    const [textValue, setTextValue] = useState(formatAmount(roundedInitialValue.toString()));
    let textFiledValue;
    if (
      roundedInitialValue.toFixed(2) ===
      parseFloat(textValue?.replaceAll(',', '') || '0').toFixed(2)
    ) {
      textFiledValue = textValue;
    } else {
      textFiledValue = formatAmount(roundedInitialValue.toString());
      setTextValue(textFiledValue);
    }

    return (
      <Stack
        ref={ref}
        sx={{
          position: 'relative',
          ...styles,
          ...sx,
        }}
      >
        <TextField
          variant="outlined"
          value={textFiledValue}
          onChange={(e) => {
            const newAmount = formatAmount(e.target.value, true);
            if (newAmount !== null) {
              setTextValue(newAmount === '' ? '0' : newAmount);
              setValue(
                name,
                newAmount === '' ? 0 : parseFloat(e.target.value.replaceAll(',', '')),
                {
                  shouldDirty: true,
                  shouldValidate: true,
                },
              );
            }
          }}
          InputProps={{
            ...(adornmentText && {
              startAdornment: (
                <InputAdornment
                  sx={{
                    ...inputAdornmentStyles,
                    ...adornmentSx,
                    '.MuiTypography-root': {
                      ...inputAdornmentStyles['.MuiTypography-root'],
                      ...(adornmentSx?.['.MuiTypography-root' as keyof typeof adornmentSx] || {}),
                    },
                  }}
                  position="start"
                >
                  {adornmentText}
                </InputAdornment>
              ),
            }),
          }}
          disabled={disabled}
        />
      </Stack>
    );
  },
);

export default ControlledContractMilestoneNumberInput;
